package fr.manyverse.app.presentation.components.modules.announcement

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Minimize
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.presentation.components.ZoomableAsyncImage
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.announcement_image_description
import manyverse.shared.generated.resources.hide_announcement_text
import manyverse.shared.generated.resources.minimize_announcement
import manyverse.shared.generated.resources.show_announcement_text
import org.jetbrains.compose.resources.stringResource

/**
 * A fullscreen overlay to display announcements to players.
 * Shows image in center with text below, or text centered if no image.
 */
@Composable
fun AnnouncementPlayerOverlay(
    announcement: ActiveAnnouncement,
    onMinimize: (announcementEventId: String) -> Unit,
    modifier: Modifier = Modifier
) {
    var isTextVisible by remember { mutableStateOf(true) }
    val focusManager = LocalFocusManager.current

    // Le Dialog prenait le focus et fermait ainsi le clavier : sans ça, un clavier ouvert
    // (chat, notes) resterait par-dessus l'annonce
    LaunchedEffect(Unit) { focusManager.clearFocus() }

    // Box plein écran dans l'arbre de PlayerScreen, et non un Dialog : sur iOS le Dialog vit dans
    // une scène et une vue Metal séparées, dont l'animation d'apparition enregistre le contenu
    // dans un GraphicsLayer ; il arrivait que seule l'image (layer enfant) soit redessinée,
    // sans le scrim ni le bouton de réduction.
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.9f))
            // Le nœud de saisie fait de l'overlay la cible du hit test : l'écran dessous ne reçoit
            // plus les touches, sans rien consommer (le zoom de l'image garde ses gestes)
            .pointerInput(Unit) {},
        contentAlignment = Alignment.Center
    ) {
        // Content
        when {
            announcement.imageUrl != null -> {
                // Image announcement
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .safeDrawingPadding()
                        .padding(2.dp),
                    contentAlignment = Alignment.Center
                ) {
                    // Blanc sur le scrim pour l'indicateur de chargement et l'icône d'erreur
                    CompositionLocalProvider(LocalContentColor provides Color.White) {
                        ZoomableAsyncImage(
                            imageUrl = announcement.imageUrl,
                            contentDescription = stringResource(Res.string.announcement_image_description),
                            modifier = Modifier.fillMaxSize()
                        )
                    }

                    // Text overlay at bottom if present (stays fixed, outside zoom)
                    if (isTextVisible && !announcement.text.isNullOrBlank()) {
                        Text(
                            text = announcement.text,
                            color = Color.White,
                            fontSize = 20.sp,
                            textAlign = TextAlign.Center,
                            modifier = Modifier
                                .align(Alignment.BottomCenter)
                                .padding(bottom = 48.dp)
                                .background(
                                    color = MaterialTheme.colorScheme.scrim.copy(alpha = 0.6f),
                                    shape = MaterialTheme.shapes.medium
                                )
                                .padding(horizontal = 24.dp, vertical = 12.dp)
                        )
                    }
                }
            }

            !announcement.text.isNullOrBlank() -> {
                // Text-only announcement
                if (isTextVisible) {
                    Text(
                        text = announcement.text,
                        color = Color.White,
                        fontSize = 24.sp,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .safeDrawingPadding()
                            .padding(32.dp)
                    )
                }
            }
        }

        // Minimize button (top-right)
        FilledIconButton(
            onClick = { onMinimize(announcement.eventId) },
            modifier = Modifier
                .align(Alignment.TopEnd)
                .safeDrawingPadding()
                .padding(16.dp)
                .size(48.dp),
            shape = CircleShape,
            colors = IconButtonDefaults.filledIconButtonColors(
                containerColor = Color.White.copy(alpha = 0.2f)
            )
        ) {
            Icon(
                imageVector = Icons.Default.Minimize,
                contentDescription = stringResource(Res.string.minimize_announcement),
                tint = Color.White,
                modifier = Modifier.size(24.dp)
            )
        }

        // Toggle text visibility button in bottom-right corner
        if (!announcement.text.isNullOrBlank() && announcement.imageUrl != null) {
            IconButton(
                onClick = { isTextVisible = !isTextVisible },
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .safeDrawingPadding()
                    .padding(16.dp)
                    .size(48.dp)
                    .background(
                        color = Color.White.copy(alpha = 0.2f),
                        shape = CircleShape
                    )
            ) {
                Icon(
                    imageVector = if (isTextVisible) Icons.Default.VisibilityOff else Icons.Default.Visibility,
                    contentDescription = stringResource(
                        if (isTextVisible) Res.string.hide_announcement_text
                        else Res.string.show_announcement_text
                    ),
                    tint = Color.White,
                    modifier = Modifier.size(24.dp)
                )
            }
        }
    }
}
